import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, ReplaySubject, mergeMap, of, timer } from 'rxjs';
import { TranslationsDTO, TranslationsResponseDTO } from '../dto/common-dto';
import {
  DEFAULT_DEBOUNCE_MIN_TIME,
  STORAGE_KEY_TYPE,
} from '../common/constants';
import { environment } from '../../environments/environment';

/**
 * LanguageHelperService is used to detect any changes regarding user's language.
 * The default language will be the first language from the available languages array.
 * When the browser language is detected or the user changes the language, the app will
 * use the selected language.
 */
@Injectable({ providedIn: 'root' })
export class LanguageHelperService {
  // Stores the translations.
  private readonly translationsSource = signal<TranslationsDTO>({});
  /**
   * The translations of the current language.
   */
  readonly translations = this.translationsSource.asReadonly();
  // Current language.
  private readonly currentLanguageSource = signal('');
  /**
   * The language currently in use.
   */
  readonly currentLanguage = this.currentLanguageSource.asReadonly();
  // Stores users's browser language.
  private userLang: string;
  // Available languages.
  readonly availableLanguages = environment.availableLanguages;
  // Tells if the language should be changed or not.
  shouldChange = true;
  /**
   * The subject used to controls the service communication.
   */
  private initLangSource = new ReplaySubject<boolean>();
  /**
   * Observable instance of the source object.
   */
  public initLangChanged = this.initLangSource.asObservable();

  // Inject TranslateService.
  private readonly translateService = inject(TranslateService);

  /**
   * Runs when Angular instantiates the component.
   */
  constructor() {
    // An EventEmitter to listen to language change events.
    this.translateService.onLangChange.subscribe((data) => {
      if (this.shouldChange) {
        this.shouldChange = false;
        timer(DEFAULT_DEBOUNCE_MIN_TIME).subscribe(() =>
          this.initLangSource.next(true)
        );
      }
      this.getCurrentLanguageAndTranslations(data as TranslationsResponseDTO);
    });

    // Add languages to the translations array.
    this.translateService.addLangs(this.availableLanguages);
    // Gets the user's browser language.
    this.userLang = navigator.language;

    const userSavedLang = window.localStorage.getItem(
      STORAGE_KEY_TYPE.STATUS_LANGUAGE
    );

    // Checks if the user language was saved locally.
    if (userSavedLang) {
      this.setLanguage(userSavedLang);
    } else if (this.userLang) {
      // Find the language in the existing array if the browser language was detected.
      let findLanguage: string | undefined;

      if (this.userLang.length === 2) {
        findLanguage = this.availableLanguages.find(
          (lang: string) => lang.split('-')[0] === this.userLang
        );
      } else {
        findLanguage = this.availableLanguages.find(
          (lang: string) => lang === this.userLang
        );
      }
      if (findLanguage && findLanguage.length > 0) {
        // If it's available then the app will use it.
        this.setLanguage(findLanguage, true);
      } else {
        // Otherwise the app will use the first language stored in the available languages array.
        this.setLanguage(this.availableLanguages[0], true);
      }
    } else {
      // Otherwise the app will use the first language stored in the available languages array.
      this.setLanguage(this.availableLanguages[0], true);
    }
  }

  /**
   * Stores the current language and its translations.
   *
   * @param language Represents the language change event.
   */
  getCurrentLanguageAndTranslations(language: TranslationsResponseDTO): void {
    this.currentLanguageSource.set(this.translateService.getCurrentLang() ?? '');
    this.translationsSource.set(language.translations as TranslationsDTO);
  }

  /**
   * Changes the language currently used.
   *
   * @param language Represents the selected language.
   * @param shouldSave Represents the flag that deteremines whether the language should be saved or not.
   */
  setLanguage(language: string, shouldSave = false): void {
    this.translateService.use(language);
    if (shouldSave) {
      window.localStorage.setItem(STORAGE_KEY_TYPE.STATUS_LANGUAGE, language);
    }
  }

  /**
   * Get the current used language.
   *
   * @returns The current used language.
   */
  getCurrentLanguage(): Observable<string> {
    return this.translateService.onLangChange.pipe(
      mergeMap((lang) => {
        return of(lang.lang);
      })
    );
  }
}
